import React, { useId, useMemo, useState } from 'react';
import { AlertCircle, CheckCircle2, CircleDashed, Loader2, Plus, Rss, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge, EmptyState, ErrorState, IconButton, LoadingState } from '@/components/primitives';
import { timeAgo } from '@/hooks/useSystemStatus';
import { adminApi, formatUtc, toastError, useAdminQuery, useBusy } from './adminApi';
import { AdminSection, Pager, RefreshError, SearchField, Toolbar } from './AdminUi';

const PAGE = 50;
// Same threshold the platform status uses to call ingestion stale.
const STALE_MS = 6 * 3600 * 1000;

function FeedHealth({ health }) {
  if (!health) return <Badge plain size="sm" icon={CircleDashed}>Not checked yet</Badge>;
  const stale = Date.now() - new Date(health.last_checked).getTime() > STALE_MS;
  if (!health.ok) {
    return (
      <span className="adm-health">
        <Badge tone="danger" plain size="sm" icon={AlertCircle}>Failing</Badge>
        <span className="adm-meta adm-break" title={health.error || undefined}>{health.error || 'No entries returned'}</span>
        {health.next_attempt && <span className="adm-meta">Next retry: {formatUtc(health.next_attempt)}</span>}
      </span>
    );
  }
  return (
    <span className="adm-health">
      <Badge tone={stale ? 'warning' : 'success'} plain size="sm" icon={stale ? AlertCircle : CheckCircle2}>{stale ? 'Stale' : 'OK'}</Badge>
      <span className="adm-meta">{health.entries ?? 0} entries{health.new_posts != null ? ` · ${health.new_posts} new` : ''}{health.collector ? ` · ${health.collector}` : ''}</span>
    </span>
  );
}

/** Ingestion feeds (RSS/Atom and Twitter URLs) with the last result for each. */
export default function FeedsTab() {
  const { data, error, loading, reload, setData } = useAdminQuery(
    () => adminApi.get('/admin/resources').then((r) => r.data.sources || []),
    []
  );
  const healthQuery = useAdminQuery(() => adminApi.get('/admin/feeds/health').then((r) => r.data.feeds || {}), []);
  const health = healthQuery.data || {};
  const [url, setUrl] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const { run, busy } = useBusy();
  const inputId = useId();

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data || []).filter((u) => !needle || u.toLowerCase().includes(needle));
  }, [data, q]);
  const failing = (data || []).filter((u) => health[u] && !health[u].ok).length;

  const add = async (value) => {
    const res = await adminApi.post('/admin/resources', { url: value });
    setData(res.data.sources || []);
  };

  const submit = (e) => {
    e.preventDefault();
    const value = url.trim();
    if (!/^https?:\/\/[^\s/]+\.[^\s]+$/i.test(value)) {
      toast.error('Enter a full feed URL starting with http:// or https://');
      return;
    }
    if ((data || []).includes(value)) {
      toast.error('That feed is already in the list');
      return;
    }
    run('add', async () => {
      try {
        await add(value);
        setUrl('');
        toast.success('Feed added', { description: 'It is fetched on the next ingestion run.' });
      } catch (err) {
        toastError(err, 'Could not add the feed');
      }
    });
  };

  const remove = (value) => run(`del-${value}`, async () => {
    try {
      const res = await adminApi.delete('/admin/resources', { data: { url: value } });
      setData(res.data.sources || []);
      toast('Feed removed', {
        description: value,
        action: {
          label: 'Undo',
          onClick: () => add(value).then(() => toast.success('Feed restored')).catch((err) => toastError(err, 'Could not restore the feed')),
        },
      });
    } catch (err) {
      toastError(err, 'Could not remove the feed');
    }
  });

  return (
    <AdminSection
      title="Feeds"
      count={data ? data.length : null}
      description="Sources the ingestion job reads. Status is the result of the scraper's most recent fetch of each URL."
    >
      <form className="adm-inline-form" onSubmit={submit}>
        <label htmlFor={inputId} className="adm-label">Add an RSS, Atom or Twitter URL</label>
        <div className="adm-inline-form-row">
          <input
            id={inputId}
            type="url"
            inputMode="url"
            className="adm-input adm-mono"
            placeholder="https://example.com/feed.xml or https://x.com/username"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            data-testid="resource-input"
          />
          <Button type="submit" size="sm" disabled={busy('add')} data-testid="add-resource-btn">
            {busy('add') ? <Loader2 className="is-spinner" aria-hidden="true" /> : <Plus aria-hidden="true" />}
            {busy('add') ? 'Adding…' : 'Add feed'}
          </Button>
        </div>
        <p className="adm-hint">
          Feeds are saved in the database and survive API restarts. Removing a feed stops future collection.
        </p>
      </form>

      <p className="adm-hint">
        Twitter supports public profiles, post links and search URLs such as https://x.com/search?q=ransomware.
        {' Collection uses free public embeds, a local browser and x-tweet-scraper. If X requires login, save your session with the supplied login script.'}
        {' Twitter collection covers a bounded set of recent posts; it does not guarantee complete timelines.'}
      </p>

      <Toolbar>
        <SearchField value={q} onChange={(v) => { setQ(v); setOffset(0); }} label="Search feeds" placeholder="Search feed URLs" />
        {failing > 0 && <Badge tone="danger" plain icon={AlertCircle}>{failing} failing</Badge>}
      </Toolbar>

      {loading ? (
        <LoadingState label="feeds" rows={6} />
      ) : error && !data ? (
        <ErrorState title="Couldn't load feeds" error={error} onRetry={reload} />
      ) : !data.length ? (
        <EmptyState icon={Rss} title="No feeds configured" description="Add an RSS, Atom or Twitter URL above to start ingesting." />
      ) : !rows.length ? (
        <EmptyState variant="compact" title="No matching feeds" description={`Nothing matched "${q}".`} />
      ) : (
        <>
          <RefreshError error={error} onRetry={reload} what="feeds" />
          {healthQuery.error && (
            <ErrorState variant="inline" title="Couldn't load feed status" error={healthQuery.error} onRetry={healthQuery.reload} />
          )}
          <div className="adm-table-wrap">
            <table className="adm-table adm-table--stack" data-testid="feeds-table">
              <thead>
                <tr>
                  <th scope="col">Feed URL</th>
                  <th scope="col">Last fetch</th>
                  <th scope="col">Checked</th>
                  <th scope="col" className="adm-col-actions"><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(offset, offset + PAGE).map((u) => (
                  <tr key={u} data-testid="resource-row">
                    <td data-label="Feed URL" className="adm-mono adm-break">{u}</td>
                    <td data-label="Last fetch">{healthQuery.loading ? <span className="adm-meta">Loading…</span> : <FeedHealth health={health[u]} />}</td>
                    <td data-label="Checked" className="adm-mono adm-nowrap adm-secondary" title={health[u] ? formatUtc(health[u].last_checked) : undefined}>
                      {health[u] ? timeAgo(health[u].last_checked) : '-'}
                    </td>
                    <td className="adm-actions-cell">
                      <IconButton
                        label={`Remove feed ${u}`}
                        icon={busy(`del-${u}`) ? Loader2 : Trash2}
                        variant="danger"
                        disabled={busy(`del-${u}`)}
                        className={busy(`del-${u}`) ? 'adm-spin' : undefined}
                        onClick={() => remove(u)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager offset={offset} limit={PAGE} total={rows.length} onChange={setOffset} label="Feed pages" />
        </>
      )}
    </AdminSection>
  );
}
